import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MapPinOff } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AlertTransactionsResponse } from '@/types/alertDetail';
import AlertMapEmbed, { type MarkerDef } from '@/components/maps/AlertMapEmbed';

interface TransactionMapSectionProps {
  query: UseQueryResult<AlertTransactionsResponse, Error>;
}

/**
 * Section 5: where the alert occurred.
 *
 * Renders a MapLibre embed pinning every source transaction that has
 * `location_lat`/`location_lon` populated (most rows in fuel/oil/toll
 * transactions have `location_geog`, so they should). Falls back to a small
 * inline empty state when no rows have geography — the rest of the alert
 * detail page still renders below.
 *
 * The full Maps page (with routing, isochrones, geofences, …) lives at
 * `/app/maps`; this is the lightweight "drop pin and go" view for
 * investigators.
 */
export function TransactionMapSection({
  query,
}: TransactionMapSectionProps): JSX.Element {
  const heading = (
    <h2 id="transaction-map" className="text-lg font-semibold">
      Transaction location
    </h2>
  );

  const txs = query.data?.transactions ?? [];

  // Stable marker reference — avoids re-running the AlertMapEmbed effect on
  // every parent re-render when the underlying transaction list hasn't
  // changed.
  const markers = useMemo<MarkerDef[]>(
    () =>
      txs
        .filter((t) => t.location_lat != null && t.location_lon != null)
        .map((t) => ({
          lat: t.location_lat as number,
          lon: t.location_lon as number,
          label:
            t.merchant_name ??
            t.station_name ??
            `Transaction ${t.transaction_id}`,
          color: '#dc2626',
        })),
    [txs],
  );

  if (query.isLoading) {
    return (
      <section aria-labelledby="transaction-map">
        {heading}
        <Skeleton className="mt-3 h-[320px] w-full" />
      </section>
    );
  }

  if (query.isError) {
    return (
      <section aria-labelledby="transaction-map">
        {heading}
        <p className="mt-2 text-sm text-destructive">
          Failed to load transactions: {query.error?.message ?? 'unknown error'}
        </p>
      </section>
    );
  }

  if (txs.length === 0) {
    return (
      <section aria-labelledby="transaction-map">
        {heading}
        <p className="mt-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          No source transactions are linked to this alert.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="transaction-map" className="space-y-3">
      {heading}

      {markers.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <MapPinOff className="h-4 w-4" />
          <span>Location data not available for this alert.</span>
        </div>
      ) : (
        <AlertMapEmbed markers={markers} height="320px" />
      )}

      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Tx ID</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Merchant / Station</TableHead>
              <TableHead className="text-right">Lat</TableHead>
              <TableHead className="text-right">Lon</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {txs.map((tx) => (
              <TableRow key={tx.transaction_id}>
                <TableCell className="font-mono text-xs">
                  {tx.transaction_id}
                </TableCell>
                <TableCell className="text-xs">
                  {new Date(tx.transaction_time)
                    .toISOString()
                    .replace('T', ' ')
                    .slice(0, 19)}
                </TableCell>
                <TableCell className="text-xs">
                  {tx.merchant_name ?? tx.station_name ?? (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {tx.location_lat != null ? (
                    tx.location_lat.toFixed(5)
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      no geog
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {tx.location_lon != null ? (
                    tx.location_lon.toFixed(5)
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      no geog
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
